/*
 * Peak Promise -- assets/pmg-availability.js
 *
 * Upgrades the card that snippets/pmg-availability.liquid already rendered.
 * It never builds the card from nothing: Liquid produced a correct, if less
 * detailed, answer server-side, and this replaces parts of it with better
 * ones -- per-shop counts, the real dispatch date, a delivery window for the
 * shopper's own postcode. If the request fails, times out, or the feature is
 * switched off in Ops, what Liquid rendered stays on screen. Nothing looks
 * broken, because nothing is.
 *
 * Two layers, in this order:
 *
 *   1. The variant map. Liquid publishes what the card WOULD SAY for every
 *      variant -- the state and the wording, never a raw quantity beyond the
 *      threshold the card prints anyway. So changing size updates the card
 *      instantly, with no request, and correctly even when the app is
 *      switched off entirely.
 *   2. The app. Adds what only the server knows: which shop is holding it,
 *      supplier stock, the PO it is coming in on, real dates.
 *
 * The rule worth keeping: layer 2 decides nothing here. Every state, count
 * and date arrives already decided by server/availability.js, so the shop's
 * answer and the customer's answer cannot drift apart. When something reads
 * wrong, the fix is almost always there, not in this file.
 */
(function () {
  "use strict";

  var TIMEOUT_MS = 8000;
  // Long enough that a fast reply never flashes a loading state, short
  // enough that a slow one does not look frozen.
  var PENDING_DELAY_MS = 180;
  var STORE_KEY = "pmg_postcode";
  var DATE_OPTS = { weekday: "short", day: "numeric", month: "short" };

  function fmtDate(iso) {
    if (!iso) return "";
    var parts = String(iso).split("-");
    if (parts.length !== 3) return "";
    // Built in local time deliberately: an ISO string parsed as UTC renders
    // as the previous day for anyone east of Greenwich, which is everyone.
    var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(d.getTime())) return "";
    try {
      return d.toLocaleDateString("en-AU", DATE_OPTS);
    } catch (e) {
      return parts[2] + "/" + parts[1];
    }
  }

  function fmtCountdown(seconds) {
    var hours = Math.floor(seconds / 3600);
    var mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return hours + "h " + mins + "m";
    if (mins > 0) return mins + " min";
    return "moments";
  }

  // 12 months reads as months; two years and up reads as years, because
  // "60 month warranty" is a number nobody converts in their head while
  // deciding whether to spend $900.
  function fmtWarranty(months) {
    if (!months) return "";
    if (months >= 24 && months % 12 === 0) return months / 12 + " year warranty";
    return months + " month warranty";
  }

  var WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

  function ordinal(n) {
    var v = n % 100;
    if (v >= 11 && v <= 13) return n + "th";
    return n + (["th", "st", "nd", "rd"][n % 10] || "th");
  }

  // "Mon 7th Sept". Built by hand rather than by toLocaleDateString because
  // no locale gives an ordinal, and because "Sept" reads better than "Sep"
  // beside a promise someone is deciding on.
  function fmtDay(iso) {
    if (!iso) return "";
    var parts = String(iso).split("-");
    if (parts.length !== 3) return "";
    var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(d.getTime())) return "";
    return WEEKDAYS[d.getDay()] + " " + ordinal(d.getDate()) + " " + MONTHS[d.getMonth()];
  }

  // 14px line icons, currentColor, no network request. Deliberately plain:
  // they are there to let the eye find the three facts, not to decorate.
  var ICONS = {
    dispatch: "M3 7l7-3 7 3v6l-7 3-7-3z M3 7l7 3 7-3 M10 10v6",
    delivery: "M1 5h9v7H1z M10 8h3l2 2v2h-5z M4.5 14a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M12.5 14a1.5 1.5 0 100-3 1.5 1.5 0 000 3z",
    collect: "M2 6h14l-1 8H3z M2 6l1.5-3h11L16 6 M6.5 9v2 M11.5 9v2"
  };

  function iconFor(kind) {
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 18 18");
    svg.setAttribute("class", "pmg-avail__icon");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    var path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", ICONS[kind]);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.3");
    path.setAttribute("stroke-linejoin", "round");
    path.setAttribute("stroke-linecap", "round");
    svg.appendChild(path);
    return svg;
  }

  // A line with an icon and a body that may carry emphasised dates.
  function iconLine(kind, className, pieces) {
    var p = el("p", "pmg-avail__line " + className);
    p.appendChild(iconFor(kind));
    var body = el("span", "pmg-avail__line-body");
    pieces.forEach(function (piece) {
      if (piece == null) return;
      if (typeof piece === "string") {
        body.appendChild(document.createTextNode(piece));
      } else {
        body.appendChild(piece);
      }
    });
    p.appendChild(body);
    return p;
  }

  // The server counts stock; the shop describes it. "2 left" is a warning,
  // "2 available for dispatch" is an answer to the question actually being
  // asked, which is whether one can be sent today.
  function availableForDispatch(display) {
    if (!display) return "In stock";
    if (/^last one$/i.test(display)) return "1 available for dispatch";
    var m = /^(\d+)\s+left$/i.exec(display);
    if (m) return m[1] + " available for dispatch";
    return display;
  }

  // Peak Moto's ETA board talks in thirds of a month - "Mid Nov '26" - and
  // the card matches it word for word, because a shopper who rings the shop
  // should hear the same answer they read here. A precise-looking date on a
  // shipment that has not left the supplier yet is a promise nobody can keep.
  function fmtEtaBoard(iso) {
    if (!iso) return "";
    var parts = String(iso).split("-");
    if (parts.length !== 3) return "";
    var day = Number(parts[2]);
    var month = Number(parts[1]) - 1;
    if (isNaN(day) || isNaN(month) || month < 0 || month > 11) return "";
    var third = day <= 10 ? "Early" : day <= 20 ? "Mid" : "Late";
    return third + " " + MONTHS[month] + " '" + String(parts[0]).slice(-2);
  }

  function strongDate(iso) {
    return el("strong", "pmg-avail__date", fmtDay(iso));
  }

  function strongText(text) {
    return el("strong", "pmg-avail__date", text);
  }

  /*
   * Click and collect is someone walking to a rack, not a courier run. When
   * the shop named on the card is holding the item on its own floor, it is
   * ready about two hours after the order lands, not on the next business
   * day the server assumes for a parcel. Anything that still has to travel
   * between shops keeps the server's date, because that one is real.
   */
  var SHOP_ZONES = [
    ["port melbourne", "Australia/Melbourne"],
    ["melbourne", "Australia/Melbourne"],
    ["west end", "Australia/Brisbane"],
    ["brisbane", "Australia/Brisbane"]
  ];

  function zoneForShop(label) {
    var needle = String(label || "").toLowerCase();
    for (var i = 0; i < SHOP_ZONES.length; i++) {
      if (needle.indexOf(SHOP_ZONES[i][0]) !== -1) return SHOP_ZONES[i][1];
    }
    return "Australia/Melbourne";
  }

  // Now, as the collecting shop's own wall clock reads it. A Perth shopper
  // collecting in Brisbane has to be told Brisbane's hours, not their own.
  function nowInZone(tz) {
    var now = new Date();
    try {
      var shifted = new Date(now.toLocaleString("en-US", { timeZone: tz }));
      if (!isNaN(shifted.getTime())) return shifted;
    } catch (e) {}
    return now;
  }

  function minutesOfClock(hhmm, fallback) {
    var m = /^(\d{1,2}):?(\d{2})?$/.exec(String(hhmm == null ? "" : hhmm).trim());
    if (!m) return fallback;
    var mins = Number(m[1]) * 60 + Number(m[2] || 0);
    return isNaN(mins) ? fallback : mins;
  }

  var DAY_TOKENS = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

  // "mon-fri 10:00-17:30; sat 10:00-16:00" -> { 1: {open, close}, ... }.
  // One string rather than a setting per day because trading hours change as
  // a set, and a day left out of it is simply a day the shop is shut.
  function parseHours(text) {
    var map = {};
    String(text == null ? "" : text).split(";").forEach(function (chunk) {
      var m = /^\s*([a-z]{3})[a-z]*\s*(?:(?:-|to)\s*([a-z]{3})[a-z]*)?\s+(\d{1,2}:?\d{0,2})\s*(?:-|to)\s*(\d{1,2}:?\d{0,2})\s*$/i.exec(chunk);
      if (!m) return;
      var from = DAY_TOKENS[m[1].toLowerCase()];
      var to = m[2] ? DAY_TOKENS[m[2].toLowerCase()] : from;
      if (from == null || to == null) return;
      var open = minutesOfClock(m[3], null);
      var close = minutesOfClock(m[4], null);
      if (open == null || close == null || close <= open) return;
      var day = from;
      for (var i = 0; i < 7; i++) {
        map[day] = { open: open, close: close };
        if (day === to) break;
        day = (day + 1) % 7;
      }
    });
    return map;
  }

  // "3pm", "2:45pm". Quarter hours only, and always rounded up, so the time
  // printed is never earlier than the moment it is genuinely ready.
  function fmtClock(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var suffix = h >= 12 && h < 24 ? "pm" : "am";
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + (m ? ":" + String(m).padStart(2, "0") : "") + suffix;
  }

  /*
   * Express Post is weight rated in Peak Moto's own delivery profile, so the
   * card can quote the price the shopper will actually be charged rather
   * than a vague "from". This is deliberately NOT an estimate of what
   * Australia Post charges Peak Moto: it mirrors the rate table Shopify uses
   * at checkout, from the same variant weights, so the two cannot disagree
   * for a single item order. Add a second item and the cart weight can climb
   * a tier, which is why the wording says "for this item".
   */
  function parseTiers(text) {
    var tiers = [];
    String(text == null ? "" : text).split(";").forEach(function (chunk) {
      var parts = chunk.split(":");
      if (parts.length !== 2) return;
      var grams = parts[0].trim();
      var price = Number(parts[1]);
      if (isNaN(price)) return;
      tiers.push({
        max: grams === "*" ? Infinity : Number(grams),
        price: price
      });
    });
    return tiers.filter(function (t) { return !isNaN(t.max); })
      .sort(function (a, b) { return a.max - b.max; });
  }

  function tierPrice(tiers, grams) {
    for (var i = 0; i < tiers.length; i++) {
      if (grams <= tiers[i].max) return tiers[i].price;
    }
    return null;
  }

  // "$25", and "$25.50" only if the rate ever has cents in it.
  function money(amount) {
    if (amount == null) return "";
    return "$" + (amount % 1 === 0 ? String(amount) : amount.toFixed(2));
  }

  function isoOfDate(d) {
    return d.getFullYear() + "-" +
      String(d.getMonth() + 1).padStart(2, "0") + "-" +
      String(d.getDate()).padStart(2, "0");
  }

  // The first moment the shop can hand it over: the lead time from now,
  // pushed forward to opening if the shop is shut, and on to the next
  // trading day if that lands after close.
  function collectSlot(cfg) {
    var now = nowInZone(cfg.tz);
    var ready = new Date(now.getTime() + cfg.leadMinutes * 60000);
    for (var i = 0; i < 8; i++) {
      var day = new Date(ready.getFullYear(), ready.getMonth(), ready.getDate() + i);
      var hours = cfg.hours[day.getDay()];
      if (!hours) continue;
      var minutes = i === 0 ? ready.getHours() * 60 + ready.getMinutes() : hours.open;
      minutes = Math.ceil(minutes / 15) * 15;
      if (minutes < hours.open) minutes = hours.open;
      // A pickup time inside the last quarter hour is not a pickup time;
      // it rolls to the next trading day rather than sending someone to a
      // shop that is locking up.
      if (minutes > hours.close - 15) continue;
      return {
        today: day.getFullYear() === now.getFullYear() &&
          day.getMonth() === now.getMonth() &&
          day.getDate() === now.getDate(),
        iso: isoOfDate(day),
        label: fmtClock(minutes)
      };
    }
    return null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // A shopper's own postcode, kept on their device only. Wrapped because
  // private browsing and blocked site data both throw on access rather than
  // returning empty, and a thrown error here would take the card with it.
  function readPostcode() {
    try {
      return (window.localStorage.getItem(STORE_KEY) || "").replace(/\D/g, "").slice(0, 4);
    } catch (e) {
      return "";
    }
  }

  function writePostcode(value) {
    try {
      window.localStorage.setItem(STORE_KEY, value);
    } catch (e) {
      /* a shopper who blocks site data simply retypes it next time */
    }
  }

  if (typeof window.customElements === "undefined" || typeof HTMLElement === "undefined") return;

  var PmgAvailability = class extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.dataset.endpoint || "";
      // Whether the wording may say "in this size" at all. Liquid works it
      // out from the product's option names; the app confirms it.
      this.sized = this.dataset.sized === "1";
      this.stockRow = this.querySelector("[data-pmg-stock]");
      this.assuranceList = this.querySelector("[data-pmg-assurance]");
      this.shopList = this.querySelector("[data-pmg-shops]");
      this.deliveryBox = this.querySelector("[data-pmg-delivery]");
      this.cache = {};
      this.timer = null;
      this.postcode = readPostcode();
      this.variants = this.readVariantMap();

      this.cartWeight = 0;
      this.lastData = null;
      this.loadCartWeight();
      var self = this;
      this.onVisible = function () {
        if (!document.hidden) self.loadCartWeight();
      };
      document.addEventListener("visibilitychange", this.onVisible);
      window.addEventListener("focus", this.onVisible);

      this.watchVariant();
      if (this.endpoint) this.load(this.dataset.variant);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
      if (this.onVisible) {
        document.removeEventListener("visibilitychange", this.onVisible);
        window.removeEventListener("focus", this.onVisible);
      }
    }

    readVariantMap() {
      var tag = this.querySelector("[data-pmg-variants]");
      if (!tag) return {};
      try {
        return JSON.parse(tag.textContent) || {};
      } catch (e) {
        return {};
      }
    }

    // The theme re-derives the current variant in three separate places and
    // the pills and the dropdown take different paths through it, so rather
    // than depend on any one of them this watches the only thing all of them
    // must agree on: the hidden id the form would actually submit.
    watchVariant() {
      var form = this.closest("form[action*='/cart/add']") ||
        document.querySelector("form[action*='/cart/add']");
      if (!form) return;
      var input = form.querySelector("[name='id']");
      if (!input) return;

      var self = this;
      var onChange = function () {
        var id = input.value;
        if (!id || id === self.dataset.variant) return;
        self.dataset.variant = id;
        // Instant, from what Liquid published. The card is never left
        // showing the previous size's answer while a request is in flight,
        // and this is the whole update when the app is switched off.
        self.applyLocal(id);
        if (self.endpoint) self.load(id);
      };
      form.addEventListener("change", onChange);
      form.addEventListener("input", onChange);
      // Themes that set the value in script fire no event at all, so the
      // attribute itself is watched too -- the same belt-and-braces the
      // back-order button label needed.
      this.observer = new MutationObserver(onChange);
      this.observer.observe(input, { attributes: true, attributeFilter: ["value"] });
    }

    applyLocal(variantId) {
      var row = this.variants[String(variantId)];
      if (!row) return;
      this.dataset.state = row.s || "";
      this.renderStock({ state: row.s, totalDisplay: row.h, localHeadline: row.h });
      if (this.shopList) this.shopList.textContent = "";
      this.renderDelivery({ state: row.s, localPromise: row.p || "", localCaveat: row.c || "" });
    }

    load(variantId) {
      if (!variantId) return;
      var self = this;
      var key = variantId + "|" + this.postcode;
      if (this.cache[key]) return this.render(this.cache[key]);

      this.setAttribute("data-loading", "true");
      this.showPending();
      var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      var timeout = setTimeout(function () {
        if (controller) controller.abort();
      }, TIMEOUT_MS);

      var url = this.endpoint + "?variant=" + encodeURIComponent(variantId) +
        (this.postcode ? "&postcode=" + encodeURIComponent(this.postcode) : "");

      fetch(url, {
        signal: controller ? controller.signal : undefined,
        headers: { Accept: "application/json" }
      })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data || data.enabled === false || data.error) return;
          // A slower answer for a size they have since changed away from
          // must not win over the one they are looking at.
          if (String(data.variant) !== String(self.dataset.variant)) return;
          self.cache[key] = data;
          self.render(data);
        })
        .catch(function () {
          // Deliberately silent. What Liquid rendered is still correct, and
          // a console error on a product page is noise a shopper cannot act
          // on.
        })
        .then(function () {
          clearTimeout(timeout);
          self.removeAttribute("data-loading");
          self.clearPending();
        });
    }

    /*
     * Per-shop stock is the one part of the card Liquid genuinely cannot
     * answer, so on a variant change those rows are briefly stale. A pill in
     * the state column says "counting", holds the row's height so nothing
     * jumps, and buys the patience to flick between sizes.
     *
     * It waits a beat first: a flash of "Loading" on a reply that arrives in
     * 200ms reads as a glitch, and most replies do arrive that fast.
     */
    showPending() {
      var self = this;
      this.clearPending();
      this.pendingTimer = setTimeout(function () {
        self.pendingTimer = null;
        if (!self.shopList) return;
        var states = self.shopList.querySelectorAll(".pmg-avail__shop-state");
        self.pendingCells = [];
        Array.prototype.forEach.call(states, function (cell) {
          self.pendingCells.push({ cell: cell, html: cell.innerHTML });
          cell.textContent = "";
          cell.appendChild(el("span", "pmg-avail__pill", "Loading"));
        });
        self.shopList.setAttribute("aria-busy", "true");
      }, PENDING_DELAY_MS);
    }

    // Puts back whatever the row said before, for the case where the request
    // fails or times out: a stuck "Loading" is worse than a stale count.
    clearPending() {
      if (this.pendingTimer) {
        clearTimeout(this.pendingTimer);
        this.pendingTimer = null;
      }
      if (this.pendingCells) {
        this.pendingCells.forEach(function (entry) {
          if (entry.cell.querySelector(".pmg-avail__pill")) {
            entry.cell.innerHTML = entry.html;
          }
        });
        this.pendingCells = null;
      }
      if (this.shopList) this.shopList.removeAttribute("aria-busy");
    }

    render(data) {
      this.dataset.state = data.state || "";
      this.renderStock(data);
      this.renderShops(data);
      this.renderDelivery(data);
      this.renderAssurance(data);
    }

    // Liquid already put a sensible version of this on the page using the
    // store default; the app knows THIS supplier's own term, so a Shoei
    // stops claiming twelve months when it carries five years.
    renderAssurance(data) {
      if (!this.assuranceList || !data.assurance) {
        // No assurance block means a consumable, where neither line applies.
        if (this.assuranceList && data.assurance === null) this.assuranceList.textContent = "";
        return;
      }
      this.assuranceList.textContent = "";
      if (data.assurance.returnDays) {
        this.assuranceList.appendChild(el("li", null, data.assurance.returnDays + " day exchange & return"));
      }
      if (data.assurance.warrantyMonths) {
        this.assuranceList.appendChild(el("li", null, fmtWarranty(data.assurance.warrantyMonths)));
      }
    }

    renderStock(data) {
      if (!this.stockRow) return;
      var tone = "ok";
      var text = data.localHeadline || "In stock";
      switch (data.state) {
        case "in_shop":
        case "available":
          if (!data.localHeadline) text = availableForDispatch(data.totalDisplay);
          break;
        case "supplier":
          // Green, not amber. It IS in stock - just not on a shop floor - and
          // the timing is carried by the dispatch line underneath rather than
          // by hedging the dot. Amber here read as a warning about something
          // we can actually supply.
          tone = "ok";
          // Never the supplier's name. Across most of this catalogue the
          // supplier is the brand, and telling a shopper their jacket is
          // sitting at REV'IT! is an invitation to go and buy it there. The
          // server does not send the name at all, so there is nothing here
          // to slip out by accident.
          // "In stock at our warehouse" and "order by Sunday to make our next
          // supplier order" cannot both be true on the same card: the first
          // says we have it, the second says we have not bought it yet. When
          // a supplier deadline is live the deadline wins - it is the only
          // honest urgency here, and it explains a dispatch date that is a
          // fortnight out. The headline steps back to what is provable.
          text = (data.supply && data.supply.label) || "Available to order in";
          if (data.order && data.order.by) text = "Available to order";
          break;
        case "incoming":
        case "backorder":
          tone = "soon";
          // A shipment that has not landed gets the ETA board's own wording,
          // never a weekday and date it cannot honour.
          var eta = (data.incoming && data.incoming.eta) || (data.supply && data.supply.eta);
          var board = fmtEtaBoard(eta);
          if (board) {
            text = "Backordered - estimated arrival " + board;
          } else {
            text = data.localHeadline || "On backorder";
          }
          break;
        default:
          tone = "out";
          if (typeof data.sized === "boolean") this.sized = data.sized;
          text = data.localHeadline || (this.sized ? "Sold out in this size" : "Sold out");
      }
      this.stockRow.textContent = "";
      var dot = el("span", "pmg-avail__dot pmg-avail__dot--" + tone);
      dot.setAttribute("aria-hidden", "true");
      this.stockRow.appendChild(dot);
      this.stockRow.appendChild(el("span", "pmg-avail__headline", text));
    }

    // Both shops, always, in the order the theme lists them - the server only
    // sends a shop that has an inventory record for this variant, so a size
    // never sent to Brisbane used to drop that row entirely and a shopper
    // reads a missing shop as a missing answer rather than a no. No record
    // means no stock, so an unmatched shop is simply "Not in stock".
    //
    // Names come from the theme, not the server: the server says "Melbourne",
    // the shopfront says "Port Melbourne, VIC".
    //
    // Only a count at or below the threshold is ever printed - the server
    // already flattened anything higher to "In stock", so nothing leaks here.
    // The theme's list of shop names, in the order it wants them shown.
    shopNames() {
      return (this.dataset.shops || "")
        .split(";")
        .map(function (n) { return n.trim(); })
        .filter(Boolean);
    }

    // "Brisbane" -> "West End (Brisbane), QLD". Falls back to whatever the
    // server said if no configured name contains it.
    canonicalShop(name) {
      var needle = String(name || "").toLowerCase();
      var found = this.shopNames().filter(function (label) {
        var a = label.toLowerCase();
        return needle && (a.indexOf(needle) !== -1 || needle.indexOf(a) !== -1);
      })[0];
      return found || name;
    }

    // Trading hours come from the theme so Amanda can change them without a
    // deploy; the defaults are only there to keep the card sane if a setting
    // is ever left blank.
    collectConfig(shopLabel) {
      var hours = parseHours(this.dataset.collectHours);
      var lead = Number(this.dataset.collectLead);
      return {
        tz: zoneForShop(shopLabel),
        leadMinutes: isNaN(lead) || lead < 0 ? 120 : lead,
        // Mon-Fri 10:00-17:30, Sat 10:00-16:00, Sunday shut. Only ever used
        // if the theme attribute is missing or unparseable.
        hours: Object.keys(hours).length ? hours : {
          1: { open: 600, close: 1050 },
          2: { open: 600, close: 1050 },
          3: { open: 600, close: 1050 },
          4: { open: 600, close: 1050 },
          5: { open: 600, close: 1050 },
          6: { open: 600, close: 960 }
        }
      };
    }

    // What Express Post costs for the size currently selected. A helmet has
    // its own flat rate and no weight test at all; everything else falls
    // through the weight tiers. A variant with no weight recorded still
    // quotes the bottom tier, because that is exactly what checkout charges
    // for a zero weight item - the card mirrors the till, it does not
    // second-guess it.
    expressPrice() {
      var flat = Number(this.dataset.expressFlat);
      if (this.dataset.expressFlat && !isNaN(flat)) return money(flat);
      var row = this.variants && this.variants[this.dataset.variant];
      if (!row || row.w == null) return "";
      var grams = Number(row.w);
      if (isNaN(grams) || grams < 0) return "";
      // Shopify rates on the weight of the WHOLE cart, so a shopper who
      // already has a jacket in there is quoted the tier they would actually
      // pay, not the tier this item would reach on its own.
      if (typeof this.cartWeight === "number" && this.cartWeight > 0) {
        grams += this.cartWeight;
      }
      var price = tierPrice(parseTiers(this.dataset.expressTiers), grams);
      return price == null ? "" : money(price);
    }

    // The cart's own weight, read once and refreshed whenever the shopper
    // comes back to the tab - adding to the cart usually means a reload or a
    // drawer, and neither fires anything this element can rely on. Failure
    // is silent and simply leaves the quote based on this item alone.
    loadCartWeight() {
      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      fetch(root.replace(/\/$/, "") + "/cart.js", { headers: { Accept: "application/json" } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var grams = Number(cart.total_weight);
          if (isNaN(grams) || grams < 0) return;
          if (grams === self.cartWeight) return;
          self.cartWeight = grams;
          if (self.lastData) self.renderDelivery(self.lastData);
        })
        .catch(function () { /* the quote falls back to this item alone */ });
    }

    // Is the collecting shop the one physically holding it? Only then is a
    // two hour promise something the shop can actually keep.
    shopHoldsStock(data, shopName) {
      var needle = String(shopName || "").toLowerCase();
      if (!needle) return false;
      var sent = data.shops || [];
      for (var i = 0; i < sent.length; i++) {
        var name = String(sent[i].name || "").toLowerCase();
        if (!name) continue;
        if (name.indexOf(needle) !== -1 || needle.indexOf(name) !== -1) {
          return sent[i].available === true && (sent[i].qty == null || sent[i].qty > 0);
        }
      }
      return false;
    }

    renderShops(data) {
      if (!this.shopList) return;
      var sent = data.shops || [];
      var canonical = this.shopNames();
      // No list from the theme means fall back to whatever the server sent,
      // rather than rendering nothing at all.
      var names = canonical.length
        ? canonical
        : sent.map(function (s) { return s.name; });

      var match = function (label) {
        var a = label.toLowerCase();
        for (var i = 0; i < sent.length; i++) {
          var b = String(sent[i].name || "").toLowerCase();
          if (b && (a.indexOf(b) !== -1 || b.indexOf(a) !== -1)) return sent[i];
        }
        return null;
      };

      this.pendingCells = null;
      this.shopList.removeAttribute("aria-busy");
      this.shopList.textContent = "";
      // Online first: most customers are nowhere near Port Melbourne or
      // West End, so the row that applies to them leads.
      this.renderOnlineRow(data, sent);
      names.forEach(function (label) {
        var shop = match(label) || { available: false };
        var low = shop.available && shop.qty != null;
        var li = el(
          "li",
          "pmg-avail__shop pmg-avail__shop--" + (shop.available ? "in" : "out") + (low ? " pmg-avail__shop--low" : "")
        );
        var name = el("span", "pmg-avail__shop-name");
        var dot = el("span", "pmg-avail__shop-dot");
        dot.setAttribute("aria-hidden", "true");
        name.appendChild(dot);
        name.appendChild(document.createTextNode(label));
        li.appendChild(name);
        // A genuine last one is worth saying by name; otherwise the useful
        // fact is that you can walk in and collect it.
        // A number reads faster than a phrase, and "1 left" sits beside
        // "2 left" without the eye having to translate "Last one".
        var state = "Not in stock";
        if (shop.available) {
          state = low
            ? (shop.qty === 1 ? "1 left" : shop.qty + " left")
            : "Ready to collect";
        }
        li.appendChild(el("span", "pmg-avail__shop-state", state));
        this.shopList.appendChild(li);
      }, this);

    }

    /*
     * The online row names a ROUTE, not a place. Peak Moto has no warehouse -
     * that stock sits with the brand's distributor - and calling it one is a
     * claim the "supplier order" line two rows below immediately contradicts.
     * "Online" claims no building, matches how the shopper is buying right
     * now, and carries the only fact they want: can it be sent, and is it
     * sitting here or being ordered in.
     *
     * It leads the list because most customers are nowhere near either shop,
     * and the heading scopes all three rows, so this one does not have to
     * make the arithmetic add up - it only has to be true.
     */
    renderOnlineRow(data, sent) {
      var label = this.dataset.onlineLabel || "Online";
      if (!label) return;

      var inShop = (sent || []).some(function (shop) {
        return shop.available && (shop.qty == null || shop.qty > 0);
      });
      var hasStock = inShop || (typeof data.total === "number" && data.total > 0);
      var supplied = !!(data.supply && data.supply.available);

      var state, tone;
      if (data.state === "sold_out") {
        state = "Sold out"; tone = "out";
      } else if (data.state === "backorder" || data.state === "incoming") {
        state = "On backorder"; tone = "unknown";
      } else if (hasStock) {
        // The old wording here read as 'boxed and going out today', which is
        // not true of stock sitting in the warehouse -- it has to reach a
        // shop first. Dispatch is the promise we can actually keep.
        state = "Ready to Dispatch"; tone = "in";
      } else if (supplied) {
        // Held at the warehouse rather than in a shop. Green, not grey: the
        // headline above already says it is in stock at our warehouse, and a
        // grey dot under that reads as a warning about nothing.
        //
        // No date here on purpose. The card carries one dated line already,
        // directly beneath this list; repeating it wraps this row on a phone
        // and says nothing new. When that line has no date to show -- the
        // supplier has no ordering schedule or lead time set in the app, so
        // bestSupplierPlan honestly returns nothing -- it prints "we will
        // confirm your dispatch date within one business day", and that is
        // the sentence that should carry the uncertainty, not this label.
        state = "In Stock"; tone = "in";
      } else {
        state = "Sold out"; tone = "out";
      }

      var li = el("li", "pmg-avail__shop pmg-avail__shop--online pmg-avail__shop--" + tone);
      var name = el("span", "pmg-avail__shop-name");
      var dot = el("span", "pmg-avail__shop-dot");
      dot.setAttribute("aria-hidden", "true");
      name.appendChild(dot);
      name.appendChild(document.createTextNode(label));
      li.appendChild(name);
      li.appendChild(el("span", "pmg-avail__shop-state", state));
      this.shopList.appendChild(li);
    }

    // Backordered and incoming are both "you can buy it, but not today", and
    // someone who is not willing to wait still wants telling when it lands.
    // Offering the waitlist alongside the buy button costs nothing: the ones
    // who will wait add to cart anyway.
    wantsWaitlist(state) {
      return state === "sold_out" || state === "backorder" || state === "incoming";
    }

    renderCollect(data, lead) {
      // Click and collect, from the shop actually holding it. Dated,
      // because "available for pickup" with no day is how someone ends up
      // driving to Port Melbourne for nothing.
      if (data.collect && data.collect.shop) {
        // Named the way the shopfront is named, not the way the server
        // abbreviates it - "Brisbane" is not a place you can walk into.
        var shopLabel = this.canonicalShop(data.collect.shop);
        // Two hours only when that shop is proven to be holding it. If the
        // unit has to come from the other shop or a supplier, the server's
        // date is the honest one and it stays.
        var slot = this.shopHoldsStock(data, data.collect.shop)
          ? collectSlot(this.collectConfig(shopLabel))
          : null;
        // "Or collect" reads as an alternative to posting it; on a
        // pickup-only product there is nothing to be an alternative to.
        var open = (lead || "Or collect") + " it in " + shopLabel;
        var collectPieces;
        if (slot) {
          collectPieces = slot.today
            ? [open + " from ", strongText(slot.label + " today"), "."]
            : [open + " from ", strongText(slot.label + " " + fmtDay(slot.iso)), "."];
        } else if (data.collect.today) {
          collectPieces = [open + " today."];
        } else {
          collectPieces = [open + " from ", strongDate(data.collect.date), "."];
        }
        this.deliveryBox.appendChild(iconLine("collect", "pmg-avail__collect", collectPieces));
      }
    }

    renderDelivery(data) {
      this.lastData = data;
      if (!this.deliveryBox) return;
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.deliveryBox.textContent = "";

      // Aerosols, lubricants and anything else flammable cannot go in the
      // post at all, so the card must not offer postage for them. The signal
      // is the pickup-only product template, which is also what Peak Moto's
      // Pickup Only delivery profile follows.
      if (this.dataset.pickupOnly === "1") {
        this.renderCollect(data, "Collect");
        this.deliveryBox.appendChild(
          el("p", "pmg-avail__caveat", "In-store only - flammable goods cannot be sent by post.")
        );
        if (this.wantsWaitlist(data.state)) {
          this.deliveryBox.appendChild(this.waitlistForm());
        }
        return;
      }

      // A real deadline for a real supplier order -- the only urgency on
      // this card that is not manufactured. It goes above the dispatch date
      // because it is the part the shopper can still act on.
      if (data.order && data.order.by) {
        var today = new Date();
        var todayIso = today.getFullYear() + "-" +
          String(today.getMonth() + 1).padStart(2, "0") + "-" +
          String(today.getDate()).padStart(2, "0");
        var deadline = el("p", "pmg-avail__deadline");
        deadline.textContent = data.order.by <= todayIso
          ? "Order today to make our next supplier order."
          : "Order by " + fmtDate(data.order.by) + " to make our next supplier order.";
        this.deliveryBox.appendChild(deadline);
      }

      var promise = el("p", "pmg-avail__promise");
      promise.setAttribute("data-pmg-promise", "");

      if (data.dispatch && data.dispatch.secondsToCutoff > 0) {
        // The shop it leaves from is deliberately not named: which shop
        // picks an order can change on Peak Moto's side after this page was
        // rendered, and a shopper does not need to know.
        promise.appendChild(document.createTextNode("Order within "));
        var countdown = el("span", "pmg-avail__countdown", fmtCountdown(data.dispatch.secondsToCutoff));
        promise.appendChild(countdown);
        promise.appendChild(document.createTextNode(" and it is dispatched today."));
        this.tick(countdown, data.dispatch.secondsToCutoff);
      } else if (data.dispatch && data.dispatch.date) {
        // A date that rests on a supplier's word is labelled as one. The
        // caveat underneath says the same thing in a sentence and people
        // do not read it; two words in the label do the work instead.
        promise = iconLine("dispatch", "pmg-avail__promise", [
          data.dispatch.estimated ? "Estimated dispatch " : "Dispatched ",
          strongDate(data.dispatch.date)
        ]);
        promise.setAttribute("data-pmg-promise", "");
      } else if (data.localPromise) {
        promise.textContent = data.localPromise;
      } else if (data.state === "backorder") {
        promise.textContent = "Order now and we will confirm your delivery date within one business day.";
      } else if (data.state === "sold_out") {
        promise.textContent = this.sized ? "Sold out in this size." : "Sold out for now.";
      } else {
        promise.textContent = "We will confirm your dispatch date within one business day.";
      }
      this.deliveryBox.appendChild(promise);

      if (data.delivery && data.delivery.from && data.delivery.to) {
        var range = data.delivery.from === data.delivery.to
          ? [strongDate(data.delivery.from)]
          : [strongDate(data.delivery.from), " to ", strongDate(data.delivery.to)];
        this.deliveryBox.appendChild(
          iconLine("delivery", "pmg-avail__eta", ["Estimated delivery "].concat(range))
        );
        // Express, the zone and the estimate caveat are all footnotes to the
        // delivery line above, so they sit indented under it rather than
        // reading as three more facts of equal weight.
        if (data.delivery.express && data.delivery.express.from) {
          var ex = el("p", "pmg-avail__caveat pmg-avail__sub");
          // The store's own rate table wins over whatever the server quoted,
          // because it is the table checkout will charge from.
          // The server already phrases its own quote as "from $25", so take
          // the "from" off whichever quote wins and let the line supply it.
          var exPrice = String(this.expressPrice() || data.delivery.express.price || "")
            .replace(/^\s*from\s+/i, "");
          // "from", because the tier is set by the weight of the whole cart
          // and the shopper can still add to it.
          ex.appendChild(document.createTextNode("Express Post from " + exPrice + ": "));
          ex.appendChild(strongDate(data.delivery.express.from));
          if (data.delivery.express.to !== data.delivery.express.from) {
            ex.appendChild(document.createTextNode(" to "));
            ex.appendChild(strongDate(data.delivery.express.to));
          }
          this.deliveryBox.appendChild(ex);
        }
        var zone = this.zoneControl(data.delivery);
        zone.className += " pmg-avail__sub";
        this.deliveryBox.appendChild(zone);
      }

      // The Australia Post estimate caveat belongs with the posting lines,
      // above click and collect - it is about the parcel, not the pickup.
      var caveatText = data.caveat || data.localCaveat;
      if (caveatText) {
        var cav = el("p", "pmg-avail__caveat pmg-avail__sub", caveatText);
        cav.setAttribute("data-pmg-caveat", "");
        this.deliveryBox.appendChild(cav);
      }

      this.renderCollect(data);

      // A sold-out size is someone who came to buy something and left with
      // nothing. This is the one place the card asks for anything back.
      if (this.wantsWaitlist(data.state)) {
        this.deliveryBox.appendChild(this.waitlistForm());
      }

    }

    // Where the estimate was worked out for, always in words. An estimate
    // that silently assumes Melbourne and shows a Perth shopper three days
    // is worse than showing no estimate at all -- so the assumption is on
    // screen, and one tap corrects it.
    zoneControl(delivery) {
      var self = this;
      var wrap = el("p", "pmg-avail__zone");
      wrap.appendChild(document.createTextNode(
        (delivery.assumed ? "Estimated for " : "Delivering to ") + (delivery.zoneLabel || "your area") + ". "
      ));

      var button = el("button", "pmg-avail__zone-btn", "Update postcode");
      button.type = "button";
      wrap.appendChild(button);

      var form = el("form", "pmg-avail__zone-form");
      form.hidden = true;
      var input = el("input", "pmg-avail__zone-input");
      input.type = "text";
      input.inputMode = "numeric";
      input.maxLength = 4;
      input.setAttribute("pattern", "[0-9]{4}");
      input.setAttribute("aria-label", "Your postcode");
      input.placeholder = "Postcode";
      input.value = this.postcode;
      var go = el("button", "pmg-avail__zone-go", "Update");
      go.type = "submit";
      form.appendChild(input);
      form.appendChild(go);
      wrap.appendChild(form);

      button.addEventListener("click", function () {
        form.hidden = false;
        button.hidden = true;
        input.focus();
      });
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        var value = input.value.replace(/\D/g, "").slice(0, 4);
        if (value.length !== 4) {
          input.focus();
          return;
        }
        self.postcode = value;
        writePostcode(value);
        self.load(self.dataset.variant);
      });
      return wrap;
    }

    waitlistForm() {
      var self = this;
      var wrap = el("div", "pmg-avail__waitlist");
      if (!this.endpoint) {
        // No app behind it means no waitlist to join. Better to say nothing
        // than to show a form that quietly goes nowhere.
        return wrap;
      }
      // "this size" is nonsense on a product with no sizes, and on a sized
      // accessory it is still the right phrase - so it follows sized, not wearable.
      var backLabel = this.sized
        ? "Tell me when this size is back"
        : "Tell me when it is back in stock";
      var label = el("label", "pmg-avail__waitlist-label", backLabel);
      label.setAttribute("for", "pmg-waitlist-email");
      wrap.appendChild(label);

      // Name first, email second. The names are optional - a restock notice
      // works without them, and an extra required field on a form someone is
      // already disappointed to be filling in costs sign-ups. They are here
      // because a notice that opens with a name reads like a shop writing to
      // a customer rather than a system firing.
      var names = el("div", "pmg-avail__waitlist-names");
      var first = el("input", "pmg-avail__waitlist-input");
      first.type = "text";
      first.id = "pmg-waitlist-first";
      first.placeholder = "First name";
      first.autocomplete = "given-name";
      first.setAttribute("aria-label", "First name (optional)");
      var last = el("input", "pmg-avail__waitlist-input");
      last.type = "text";
      last.id = "pmg-waitlist-last";
      last.placeholder = "Last name";
      last.autocomplete = "family-name";
      last.setAttribute("aria-label", "Last name (optional)");
      names.appendChild(first);
      names.appendChild(last);
      wrap.appendChild(names);

      var form = el("form", "pmg-avail__waitlist-form");
      var input = el("input", "pmg-avail__waitlist-input");
      input.type = "email";
      input.id = "pmg-waitlist-email";
      input.required = true;
      input.placeholder = "Your email";
      input.autocomplete = "email";
      var go = el("button", "pmg-avail__waitlist-go", "Let me know");
      go.type = "submit";
      form.appendChild(input);
      form.appendChild(go);
      wrap.appendChild(form);

      form.addEventListener("submit", function (event) {
        event.preventDefault();
        if (!input.value) return;
        go.disabled = true;
        go.textContent = "Sending";
        fetch(self.endpoint.replace(/availability$/, "waitlist"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            variant: self.dataset.variant,
            email: input.value,
            firstName: first.value.trim(),
            lastName: last.value.trim()
          })
        })
          .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
          })
          .then(function () {
            var who = first.value.trim();
            var thanks = who ? "Thanks, " + who + " - we" : "Thanks - we";
            var addr = input.value.trim();
            wrap.textContent = "";
            wrap.appendChild(el(
              "p",
              "pmg-avail__waitlist-done",
              thanks + " will email you at " + addr +
                (self.sized
                  ? " the moment this size is back in stock."
                  : " the moment it is back in stock.")
            ));
          })
          .catch(function () {
            go.disabled = false;
            go.textContent = "Try again";
            // Said plainly, with a way forward. An error a shopper cannot
            // act on is just an apology.
            if (!wrap.querySelector(".pmg-avail__waitlist-error")) {
              wrap.appendChild(el(
                "p",
                "pmg-avail__waitlist-error",
                "That did not send. Please try again, or email hello@peakmotogear.com.au."
              ));
            }
          });
      });
      return wrap;
    }

    tick(node, seconds) {
      var remaining = seconds;
      var self = this;
      this.timer = setInterval(function () {
        remaining -= 30;
        if (remaining <= 0) {
          clearInterval(self.timer);
          self.timer = null;
          // The cutoff passed while they were reading. Ask again rather
          // than counting down to a promise that has expired.
          self.cache = {};
          self.load(self.dataset.variant);
          return;
        }
        node.textContent = fmtCountdown(remaining);
      }, 30000);
    }
  };

  if (!window.customElements.get("pmg-availability")) {
    window.customElements.define("pmg-availability", PmgAvailability);
  }
})();
